import { useMutation, useQueryClient } from '@tanstack/react-query';

import { CreateEventRequestPayload } from '@shared/types/event';

import API from '@api/API';

import { useCurrentUserGagglRankQuery } from '@queries/CritiqueQueries';
import {
  EventInterestOption,
  EventRsvpOption,
  FeedEvent,
  useCurrentUserEventRsvpsQuery,
  useEventCompetitionDataQuery,
} from '@queries/EventQueries';

export type CreateEventMutationPayload = CreateEventRequestPayload;

interface MarkDuplicateEventPayload {
  duplicateEventId: string;
  status?: string;
}

export const useMarkDuplicateEventMutation = ({ eventId: primaryEventId }: { eventId: string }) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync: markDuplicateEventAsync } = useMutation({
    mutationFn: (payload: MarkDuplicateEventPayload) => {
      return API.post(`/v1/events/${primaryEventId}/mark-duplicate`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  return {
    isSubmitting,
    markDuplicateEventAsync,
  };
};

export interface CreateEventMutationResponse {
  event: {
    _id: string;
  };
}

export const useCreateEventMutation = () => {
  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreateEventRequestPayload) => {
      return API.post<CreateEventMutationResponse, CreateEventRequestPayload>(
        `/v1/user/events`,
        payload,
      );
    },
  });

  return {
    isCreatingEvent: isSubmitting,
    createEventAsync: mutateAsync,
  };
};

export interface UpdateEventPayload {
  isActive?: boolean;
  title?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventImage?: any;
  updatedForAccuracy?: boolean;
  cityId?: string;
  venue?: {
    _id?: string;
    title?: string;
  };
  entertainerIds?: string[];
  tagIds?: string[];
  isRecurring?: boolean;
  status?: 'approved' | 'rejected';
  recurringSchedule?: {
    type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
  };
  referralUrls?: Record<string, true>;
  featuredSlideCopy?: {
    primaryComment: string;
    secondaryComments: string[];
  };
  externalLinkButton?: {
    text?: string;
    url: string;
  };
  featuredSlideQuotes?: {
    primaryComment: {
      text: string;
      reviewId?: string;
    };
    secondaryComments: {
      text: string;
      reviewId?: string;
    }[];
  };
}

export const useUpdateEventMutation = ({ eventId }: { eventId: string }) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync: updateEventAsync } = useMutation({
    mutationFn: (payload: UpdateEventPayload) => {
      return API.put(`/v1/events/${eventId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  return {
    isSubmitting,
    updateEventAsync,
  };
};

export const useDynamicUpdateEventMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync: updateEventAsync } = useMutation({
    mutationFn: ({ eventId, ...payload }: UpdateEventPayload & { eventId: string }) => {
      return API.put(`/v1/events/${eventId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  return {
    isSubmitting,
    updateEventAsync,
  };
};

export interface RsvpToEventPayload {
  rsvp?: EventRsvpOption;
  isLiked?: boolean;
  isStarred?: boolean;
  eventInterest?: EventInterestOption;
  interested?: string[];
  notInterested?: string[];
  comment?: string;
  referredBy?: string;
  /** From a dynamic event; approves its pending source event */
  actionToken?: string;
}
export const useRsvpToEventMutation = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (params: RsvpToEventPayload & { eventId: string }) => {
      const { eventId, ...payload } = params;
      return API.post<{ event?: Pick<FeedEvent, 'chips'> }, RsvpToEventPayload>(
        `/v1/user/events/${eventId}/rsvp`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useCurrentUserEventRsvpsQuery.queryKey() });
      queryClient.invalidateQueries({ queryKey: useCurrentUserGagglRankQuery.queryKey() });
    },
  });

  return {
    isSubmitting: isPending,
    rsvpToEventAsync: mutateAsync,
  };
};

interface SubmitBonusTipVotePayload {
  eventId: string;
  distinctId: string;
  seriesBonusTipId: string;
  regionId: string[];
  showEventReviewSplash: boolean;
  referredBy?: string;
}

export const useSubmitBonusTipVoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      eventId,
      distinctId,
      seriesBonusTipId,
      regionId,
      showEventReviewSplash,
      referredBy,
    }: SubmitBonusTipVotePayload) =>
      API.post<{}, Omit<SubmitBonusTipVotePayload, 'eventId'>>(
        `/v1/events/${eventId}/bonus-tip-vote`,
        {
          distinctId,
          seriesBonusTipId,
          regionId,
          showEventReviewSplash,
          referredBy,
        },
      ),
    onSuccess: () => {},
  });

  return {
    isSubmittingBonusTipVote: isPending,
    submitBonusTipVoteAsync: mutateAsync,
  };
};

interface ToggleEventCompetitionLikePayload {
  eventId: string;
  entertainerId: string;
  isLiked: boolean;
}

export const useToggleEventCompetitionLikeMutation = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ eventId, entertainerId, isLiked }: ToggleEventCompetitionLikePayload) =>
      API.post(`/v1/events/${eventId}/competition-likes`, { entertainerId, isLiked }),
    onSuccess: (_data, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: useEventCompetitionDataQuery.queryKey(eventId) });
    },
  });

  return {
    isTogglingEventCompetitionLike: isPending,
    toggleEventCompetitionLikeAsync: mutateAsync,
  };
};

interface SubmitFlyerRequestPayload {
  eventId?: string;
  distinctId: string;
  base64Image: string;
  title?: string;
  startDate: string;
  startTime: string;
  seriesId: string;
  venue?: {
    _id?: string;
    title: string;
  };
}
export const useSubmitFlyerMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: SubmitFlyerRequestPayload) =>
      API.post<{ feedEvent: FeedEvent }, SubmitFlyerRequestPayload>(`/v1/flyers`, payload),
    onSuccess: () => {},
  });

  return {
    isSubmittingFlyer: isPending,
    submitFlyerAsync: mutateAsync,
  };
};
